import { useState } from 'react';
import { useReduxState } from 'hooks';
import { getMessageWithPlaceholders } from 'shared-locales';
import { useApiData } from 'lib/api';
import { PageHeader } from 'components/page';
import { RichTextValue } from 'components/common';
import { Box } from 'components/common/box';
import {
  HomeHeaderAdminCounts,
  HomeHeaderSearch,
  HomeHeaderShowInfo,
  HomeHeaderUserCounts,
  HomeQuickLinks
} from '.';

const HomeHeader = () => {
  const {
    language,
    messages,
    serverConfig: {
      enableGroupInfo,
      homePageInfoEnabled,
      homePageInfoTitle,
      homePageInfoTitleTranslations,
      homePageInfoText,
      homePageInfoTextTranslations,
      homePageQuickLinks
    },
    theme,
    user
  } = useReduxState();

  const [showInfo, setShowInfo] = useState<boolean>(false);

  const { data } = useApiData({
    url: `/admin/users/${user?.id}/groups`,
    onlyIf: enableGroupInfo
  });

  const groupInfoGroups =
    data?.groups.filter((group) => group.infoText && group.infoTextEnable) ||
    [];

  const infoTitle =
    homePageInfoTitleTranslations?.[language] || homePageInfoTitle;
  const infoText = homePageInfoTextTranslations?.[language] || homePageInfoText;

  const infoAvailable =
    (data || !enableGroupInfo) &&
    ((homePageInfoEnabled && (infoTitle || infoText)) ||
      groupInfoGroups?.length);

  const hours = new Date().getHours();

  const greeting = getMessageWithPlaceholders({
    message:
      hours < 6
        ? messages.homePageGreetingDefault
        : hours < 12
        ? messages.homePageGreetingMorning
        : hours < 17
        ? messages.homePageGreetingAfternoon
        : hours < 22
        ? messages.homePageGreetingEvening
        : messages.homePageGreetingDefault,
    placeholders: { name: user?.firstName || user?.username }
  });

  return (
    <div className="header">
      <PageHeader icon="home" title={greeting}>
        <div className="header-content">
          <div className="header-search">
            <HomeHeaderSearch />
          </div>

          {infoAvailable ? (
            <div className="header-settings">
              <HomeHeaderShowInfo
                onChange={(value) => setShowInfo(value)}
                groups={data?.groups || []}
              />
            </div>
          ) : null}
        </div>
      </PageHeader>

      <div className="container">
        {infoAvailable && showInfo ? (
          <div className="info">
            {homePageInfoEnabled && (infoTitle || infoText) ? (
              <div className="info-item">
                <Box>
                  {infoTitle ? (
                    <div className="info-title">{infoTitle}</div>
                  ) : null}

                  {infoText ? (
                    <div className="info-text">
                      <RichTextValue html={infoText} />
                    </div>
                  ) : null}
                </Box>
              </div>
            ) : null}

            {groupInfoGroups?.length
              ? groupInfoGroups.map((group) => (
                  <div className="info-item" key={group.id}>
                    <Box>
                      <div className="info-title">
                        {group.infoTextTitle || group.title}
                      </div>

                      {group.infoText ? (
                        <div className="info-text">
                          <RichTextValue html={group.infoText} />
                        </div>
                      ) : null}
                    </Box>
                  </div>
                ))
              : null}
          </div>
        ) : null}

        <div className="counts">
          <HomeHeaderAdminCounts />
          <HomeHeaderUserCounts />
        </div>

        {homePageQuickLinks?.links?.length ? (
          <div className="quick-links">
            <HomeQuickLinks />
          </div>
        ) : null}
      </div>

      <style jsx>{`
        .header-content {
          align-items: flex-start;
          display: flex;
          justify-content: space-between;
          gap: 15px;
        }
        .header-search {
          position: relative;
          top: -1px;
          width: 300px;
        }
        .info {
          margin-bottom: 40px;
        }
        .info-item {
          margin-top: 20px;
        }
        .info-title {
          color: ${theme.textColor};
          font-size: 1.5rem;
          font-weight: 600;
        }
        .info-text {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          font-weight: 400;
          margin-top: 10px;
        }
        .counts {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 30px;
          margin-bottom: 40px;
          margin-top: 20px;
        }
        .quick-links {
          margin-bottom: 40px;
        }
      `}</style>
    </div>
  );
};

export default HomeHeader;
